import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ProductTypeContext = createContext();
export const useProductTypes = () => useContext(ProductTypeContext);

export const ProductTypeProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [productTypes, setProductTypes] = useState([]);
  const [producttypeloading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const PRODUCTTYPE_ENDPOINT = "/product-types"; // 👈 change if API path differs
  const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;

  /* -------------------- Mount Safety -------------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetProductTypes = (updater) => {
    if (!mountedRef.current) return;
    setProductTypes(updater);
  };

  /* -------------------- FETCH ALL -------------------- */
  const fetchProductTypes = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${PRODUCTTYPE_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      safeSetProductTypes(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[ProductTypes] fetch error:", err);
      if (err.message?.includes("Token")) logout();
      safeSetProductTypes([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* -------------------- CREATE -------------------- */
  const addProductType = useCallback(
    async (data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${PRODUCTTYPE_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchProductTypes();
        return payload;
      } catch (err) {
        console.error("[ProductTypes] add error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchProductTypes]
  );

  /* -------------------- UPDATE -------------------- */
  const updateProductType = useCallback(
    async (id, data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${PRODUCTTYPE_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchProductTypes();
        return payload;
      } catch (err) {
        console.error("[ProductTypes] update error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchProductTypes]
  );

  /* -------------------- DELETE -------------------- */
  const deleteProductType = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${PRODUCTTYPE_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetProductTypes((prev) =>
          prev.filter((p) => String(p.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[ProductTypes] delete error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------- TOGGLE STATUS -------------------- */
  const toggleStatus = useCallback(

    async (id, currentStatus) => {
     const newStatus =
  currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        // Optimistic UI
        safeSetProductTypes((prev) =>
          prev.map((p) =>
            String(p.id) === String(id) ?{ ...p, status: newStatus }: p
          )
        );

        const res = await fetch(
          `${baseURL}${PRODUCTTYPE_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        return payload;
      } catch (err) {
        console.error("[ProductTypes] toggleStatus error:", err);

        // Rollback UI
        safeSetProductTypes((prev) =>
          prev.map((p) =>
            String(p.id) === String(id)
              ? { ...p, status: currentStatus }
              : p
          )
        );
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------- INITIAL LOAD -------------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchProductTypes().catch(() => {});
  }, [isReady, token, fetchProductTypes]);

  return (
    <ProductTypeContext.Provider
      value={{
        productTypes,
        producttypeloading,
        fetchProductTypes,
        addProductType,
        updateProductType,
        deleteProductType,
        toggleStatus,
      }}
    >
      {children}
    </ProductTypeContext.Provider>
  );
};
